import { TEAM_ROLES, type TeamRole } from "@sebas/core";
import { Alert, Avatar, Badge } from "@hakistudio/hakiui";
import { formatDistanceToNow } from "date-fns";
import { Check, Copy, Plus, UserMinus, Users } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  useCreateTeam, useInvite, useRemoveMember, useSetMemberRole, useTeam, useTeamActivity, useTeams,
} from "@/lib/teams";
import { Input, Select } from "@/components/ui/Field";

export function TeamsPage() {
  const { data, isPending } = useTeams();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const create = useCreateTeam();

  const teams = data?.teams ?? [];
  const activeId = selected ?? teams[0]?.id ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-5xl font-semibold lg:text-6xl">Teams</h1>
        <Button size="sm" onClick={() => setCreating((v) => !v)}>
          <Plus className="h-4 w-4" /> New team
        </Button>
      </div>

      {creating ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(name.trim(), {
              onSuccess: (r) => {
                setSelected(r.team.id);
                setName("");
                setCreating(false);
              },
            });
          }}
        >
          <Input
            autoFocus
className="flex-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Team name"
          />
          <Button type="submit" disabled={create.isPending}>Create</Button>
        </form>
      ) : null}

      {isPending ? (
        <p className="text-sm text-ink-faint">Loading…</p>
      ) : teams.length === 0 ? (
        <div className="rounded-card border border-dashed border-line px-6 py-14 text-center">
          <Users className="mx-auto h-6 w-6 text-ink-faint" />
          <p className="font-display mt-3 text-2xl font-semibold">No teams yet</p>
          <p className="mt-1 text-sm text-ink-muted">Create one to share notes and calendars.</p>
        </div>
      ) : (
        <>
          <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {teams.map((t) => (
              <Chip key={t.id} active={activeId === t.id} onClick={() => setSelected(t.id)}>
                {t.name}
              </Chip>
            ))}
          </div>
          {activeId ? <TeamDetail teamId={activeId} /> : null}
        </>
      )}
    </div>
  );
}

function TeamDetail({ teamId }: { teamId: string }) {
  const { data, isPending } = useTeam(teamId);
  const { data: act } = useTeamActivity(teamId);
  const invite = useInvite(teamId);
  const setRole = useSetMemberRole(teamId);
  const removeMember = useRemoveMember(teamId);

  const [email, setEmail] = useState("");
  const [role, setRoleValue] = useState<TeamRole>("editor");
  const [link, setLink] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(false);
  const [notified, setNotified] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isPending || !data) return <p className="text-sm text-ink-faint">Loading team…</p>;

  const canManage = data.role === "owner" || data.role === "admin";

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">Members</h2>
        <ul className="divide-y divide-line overflow-hidden rounded-card border border-line bg-raised">
          {data.members.map((m) => (
            <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
              <Avatar name={m.displayName ?? "?"} size="md" radius="full" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-ink">{m.displayName ?? "Unnamed"}</span>
                <span className="block truncate text-xs text-ink-faint">
                  joined {formatDistanceToNow(new Date(m.joinedAt), { addSuffix: true })}
                </span>
              </span>

              {canManage ? (
                <Select
                  className="w-28 shrink-0"
                  size="sm"
value={m.role}
                  onChange={(role) => {
                    setError(null);
                    setRole.mutate(
                      { userId: m.userId, role: role as TeamRole },
                      { onError: (err) => setError(err instanceof ApiError ? err.message : String(err)) },
                    );
                  }}
                  options={TEAM_ROLES.map((r) => ({
                    label: r,
                    value: r,
                    // Only an owner may mint another owner; the API enforces this too.
                    disabled: r === "owner" && data.role !== "owner",
                  }))}
                />
              ) : (
                <Badge size="sm" variant="soft" radius="full">{m.role}</Badge>
              )}

              {canManage ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${m.displayName ?? "member"}`}
                  onClick={() => {
                    setError(null);
                    removeMember.mutate(m.userId, {
                      onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
                    });
                  }}
                >
                  <UserMinus className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {error ? <Alert variant="danger" title={error} /> : null}
      </section>

      {canManage ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-ink-muted">Invite someone</h2>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              setCopied(false);
              invite.mutate(
                { email: email.trim(), role },
                {
                  onSuccess: (r) => {
                    setLink(`${window.location.origin}${r.acceptUrl}`);
                    setEmailed(r.emailed);
                    setNotified(r.notified);
                    setEmail("");
                  },
                  onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
                },
              );
            }}
          >
            <Input
              type="email"
              required
className="min-w-52 flex-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="their@email.com"
            />
            <Select
value={role}
              onChange={(v) => setRoleValue(v as TeamRole)}
              options={TEAM_ROLES.map((r) => ({
                label: r,
                value: r,
                disabled: r === "owner" && data.role !== "owner",
              }))}
            />
            <Button type="submit" disabled={invite.isPending}>Invite</Button>
          </form>

          {link ? (
            <div className="flex items-center gap-2 rounded-2xl bg-sunken px-4 py-3">
              <code className="min-w-0 flex-1 truncate text-xs text-ink-muted">{link}</code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(link);
                  setCopied(true);
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
          ) : null}
          <p className="text-xs text-ink-faint">
            {link
              ? [
                  emailed && "Invite emailed.",
                  notified && "They'll also see it in Notifications.",
                  !emailed && !notified && "Email didn't send — copy the link below.",
                  "It expires in 7 days.",
                ]
                  .filter(Boolean)
                  .join(" ")
              : "If they have a Sebas account they'll see it in Notifications. We'll also email them."}
          </p>

          {data.invites.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {data.invites.map((i) => (
                <li key={i.id} className="flex items-center gap-2 text-xs text-ink-muted">
                  <span className="truncate">{i.email}</span>
                  <Badge size="sm" variant="outline" radius="full">{i.role}</Badge>
                  <span className="text-ink-faint">
                    expires {formatDistanceToNow(new Date(i.expiresAt), { addSuffix: true })}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">Recent activity</h2>
        {(act?.activity ?? []).length === 0 ? (
          <p className="text-sm text-ink-faint">Nothing yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {act!.activity.map((a) => (
              <li key={a.id} className={cn("flex items-baseline gap-2 text-sm")}>
                <span className="text-ink">{a.actorName ?? "Someone"}</span>
                <span className="text-ink-muted">{a.action} a {a.entityType}</span>
                <span className="ml-auto shrink-0 text-xs text-ink-faint">
                  {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
